import { Text, View, ScrollView, TouchableOpacity, RefreshControl, useWindowDimensions } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { LegendList } from "@legendapp/list";
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ProfileCard from '@/components/People/ProfileCard';
import Avatar from '@/components/People/Avatar';
import { deleteSmallGroup, saveSmallGroup } from '@/data/smallGroups';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import PageTitle from '@/components/General/PageTitle';
import { saveToRecents } from '@/utils/recentlyVisited';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Unicon from '@/components/General/Unicon';
import SmallGroupSessionsTable from '@/components/SmallGroups/SmallGroupSessionsTable';
import ContentLoader from '@/components/General/ContentLoader';
import ActionButton from '@/components/General/ActionButton';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import React from 'react';

// ============ HELPER ============
const STATUSES = ["Active", "Completed", "Canceled", "Paused"];

const getStatusColors = (status) => {
    switch (status) {
        case "ACTIVE":
            return { bg: "successContainer", text: "success" };
        case "COMPLETED":
            return { bg: "infoContainer", text: "info" };
        case "CANCELED":
            return { bg: "errorContainer", text: "error" };
        case "PAUSED":
            return { bg: "warningContainer", text: "warning" };
        default:
            return { bg: "surface", text: "onSurface" };
    }
};

// ============ INFO ROW ============
const InfoRow = ({ icon, label, value, onPress, children, isEmpty }) => {
    if (!value && !children && !isEmpty) return null;
    const content = (
        <View style={styles.infoRow}>
            <View style={styles.infoRowLeft}>
                <Unicon name={icon} size={18} colorVariant="textSecondary" />
                <Text style={styles.infoRowLabel}>{label}</Text>
            </View>
            <View style={styles.infoRowRight}>
                {children || <Text style={[styles.infoRowValue, onPress && styles.linkText, isEmpty && styles.emptyText]}>{value}</Text>}
            </View>
        </View>
    );
    return onPress ? <TouchableOpacity onPress={onPress}>{content}</TouchableOpacity> : content;
};

// ============ INFO PANEL (isolated component) ============
const SmallGroupInfoPanel = React.memo(({ smallGroupID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: smallGroup, isFetching } = Query.smallGroups.byId({ id: smallGroupID });

    const saveSmallGroupMutation = useMutation({
        mutationFn: saveSmallGroup,
        onMutate: async (newData) => {
            await queryClient.cancelQueries({ queryKey: QueryKeys.smallGroups.byId(smallGroupID) });
            const prev = queryClient.getQueryData(QueryKeys.smallGroups.byId(smallGroupID));
            queryClient.setQueryData(QueryKeys.smallGroups.byId(smallGroupID), (old) => ({ ...old, ...newData }));
            return { prev };
        },
        onError: (_err, _newData, ctx) => {
            queryClient.setQueryData(QueryKeys.smallGroups.byId(smallGroupID), ctx.prev);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['smallGroups'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            queryClient.invalidateQueries({ queryKey: ['people'] });
        }
    });

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.smallGroups.byId(smallGroupID) });
    const statusColors = getStatusColors(smallGroup?.status);

    return (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <Animated.View entering={FadeInDown.duration(400)} style={styles.headerCard}>
                    {/* Type Badge */}
                    {smallGroup?.SmallGroupType?.name && (
                        <View style={styles.typeBadge}>
                            <Unicon name="book" size={14} colorVariant="groups" />
                            <Text style={styles.typeBadgeText}>{smallGroup.SmallGroupType.name}</Text>
                        </View>
                    )}

                    {/* Title Row */}
                    <View style={styles.titleRow}>
                        <View style={styles.iconBadge}>
                            <Unicon name="people" size={24} colorVariant="groups" />
                        </View>
                        <View style={styles.titleInfo}>
                            <Text style={styles.groupTitle}>{smallGroup?.name}</Text>
                            {smallGroup?.Leader && (
                                <TouchableOpacity onPress={() => router.push(`/people/${smallGroup.Leader.id}`)}>
                                    <Text style={styles.leaderSubtitle}>Led by {smallGroup.Leader.firstName} {smallGroup.Leader.lastName}{smallGroup?.CoLeader ? ` & ${smallGroup.CoLeader.firstName} ${smallGroup.CoLeader.lastName}` : ''}</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    {/* Stats Row */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Members</Text>
                            <Text style={styles.statValue}>{smallGroup?.Students?.length || 0}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Sessions</Text>
                            <Text style={styles.statValue}>{smallGroup?.Event?.EventInstances?.length || 0}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <DropdownMenu
                                testID="StatusDropdown"
                                options={STATUSES.map(status => ({
                                    name: status,
                                    disabled: status.toUpperCase() == smallGroup?.status,
                                    onSelect: (option) => saveSmallGroupMutation.mutate({ id: smallGroup.id, status: option.name.toUpperCase() })
                                }))}
                                triggerComponent={
                                    <View style={styles.statusPill(statusColors.bg, statusColors.text)}>
                                        <Text style={styles.statusPillText(statusColors.text)}>{smallGroup?.status || '-'}</Text>
                                        <Unicon name="chevron-down" size={12} colorVariant={statusColors.text} />
                                    </View>
                                }
                            />
                        </View>
                    </View>

                    {/* Leader Avatar Row */}
                    {smallGroup?.Leader && (
                        <View style={styles.leaderRow}>
                            <Avatar size={40} profile={smallGroup.Leader} disableBorder onPress={() => router.push(`/people/${smallGroup.Leader.id}`)} />
                            <View style={styles.leaderInfo}>
                                <Text style={styles.leaderLabel}>Leader</Text>
                                <Text style={styles.leaderName}>{smallGroup.Leader.firstName} {smallGroup.Leader.lastName}</Text>
                            </View>
                        </View>
                    )}

                    {/* CoLeader Avatar Row */}
                    {smallGroup?.CoLeader && (
                        <View style={styles.leaderRow}>
                            <Avatar size={40} profile={smallGroup.CoLeader} disableBorder onPress={() => router.push(`/people/${smallGroup.CoLeader.id}`)} />
                            <View style={styles.leaderInfo}>
                                <Text style={styles.leaderLabel}>Co-Leader</Text>
                                <Text style={styles.leaderName}>{smallGroup.CoLeader.firstName} {smallGroup.CoLeader.lastName}</Text>
                            </View>
                        </View>
                    )}

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        {(loggedInUser?.id == smallGroup?.leaderID || loggedInUser?.id == smallGroup?.coLeaderID || loggedInUser?.can?.("Edit All Small Groups")) && (
                            <ActionButton
                                iconName="add"
                                text="Log Session"
                                onPress={() => router.push({ pathname: 'smallgroups/sessions/create', params: { smallGroupID: smallGroup.id } })}
                            />
                        )}
                        {(loggedInUser?.id == smallGroup?.leaderID || loggedInUser?.id == smallGroup?.coLeaderID || loggedInUser?.can?.("Edit All Small Groups")) && (
                            <ActionButton
                                iconName="create"
                                text="Edit"
                                onPress={() => {
                                    const lessonTime = smallGroup?.lessonTime ? Date.parse("2022-01-01 " + smallGroup?.lessonTime) : null;
                                    router.push({ pathname: 'smallgroups/create', params: { id: smallGroup.id, lessonTime: lessonTime } });
                                }}
                            />
                        )}
                    </View>
                </Animated.View>

                {/* Info Section */}
                <Animated.View entering={FadeInDown.delay(150).duration(400)} style={styles.infoSection}>
                    <Text style={styles.sectionLabel}>Details</Text>
                    <InfoRow icon="text" label="Name" value={smallGroup?.name} />
                    {smallGroup?.lessonTime && <InfoRow icon="time" label="Lesson Time" value={smallGroup.lessonTime} />}
                    {smallGroup?.lessonDay && <InfoRow icon="calendar" label="Lesson Day" value={smallGroup.lessonDay} />}
                </Animated.View>

                {/* Custom Fields */}
                {(() => {
                    const customFields = smallGroup?.SmallGroupType?.CustomFields || [];
                    const customFieldValues = smallGroup?.customFieldValues || {};
                    const fieldsWithValues = customFields.filter(f => {
                        const v = customFieldValues[f.id];
                        return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
                    });
                    if (!fieldsWithValues.length) return null;
                    return (
                        <Animated.View entering={FadeInDown.delay(225).duration(400)} style={styles.infoSection}>
                            <Text style={styles.sectionLabel}>Custom Fields</Text>
                            {fieldsWithValues.map(field => {
                                const raw = customFieldValues[field.id];
                                let display;
                                if (field.type === 'Checkbox') display = raw ? 'Yes' : 'No';
                                else if (Array.isArray(raw)) display = raw.join(', ');
                                else display = String(raw);
                                return <InfoRow key={field.id} icon="reader-outline" label={field.name} value={display} />;
                            })}
                        </Animated.View>
                    );
                })()}

                {/* Members Preview */}
                {smallGroup?.Students?.length > 0 && (
                    <Animated.View entering={FadeInDown.delay(300).duration(400)} style={styles.studentsPreview}>
                        <Text style={styles.sectionLabel}>Members ({smallGroup.Students.length})</Text>
                        <View style={styles.studentsAvatarRow}>
                            {smallGroup.Students.slice(0, 6).map((student) => (
                                <Avatar key={student.memberID} size={36} profile={student.Person} onPress={() => router.push(`/people/${student.Person?.id}`)} />
                            ))}
                            {smallGroup.Students.length > 6 && (
                                <View style={styles.moreBadge}>
                                    <Text style={styles.moreBadgeText}>+{smallGroup.Students.length - 6}</Text>
                                </View>
                            )}
                        </View>
                    </Animated.View>
                )}
            </ScrollView>
        </View>
    );
});

// ============ STUDENTS TAB (isolated component) ============
const StudentsTab = React.memo(({ smallGroupID }) => {
    const queryClient = useQueryClient();
    const { data: smallGroup } = Query.smallGroups.byId({ id: smallGroupID });
    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.smallGroups.byId(smallGroupID) });

    return (
        <View style={styles.tabContent}>
            {smallGroup?.Students?.length > 0 ? (
                <LegendList
                    keyExtractor={(item) => item.memberID}
                    contentContainerStyle={styles.listPadding}
                    showsVerticalScrollIndicator={false}
                    data={smallGroup.Students}
                    renderItem={({ item }) => <ProfileCard key={item.id} profile={item.Person} minimal />}
                    refreshing={false}
                    progressViewOffset={1000}
                    onRefresh={refresh}
                    estimatedItemSize={100}
                    keyboardShouldPersistTaps="handled"
                />
            ) : (
                <EmptyListPlaceholder message="No members in this Small Group" />
            )}
        </View>
    );
});

// ============ SESSIONS TAB (isolated component) ============
const SessionsTab = React.memo(({ smallGroupID }) => {
    const { data: smallGroup } = Query.smallGroups.byId({ id: smallGroupID });
    return (
        <View style={styles.tabContent}>
            <SmallGroupSessionsTable
                smallGroupID={smallGroupID}
                title="Sessions"
                description={`All sessions for ${smallGroup?.name || 'this group'}`}
                showSmallGroupInfo={false}
            />
        </View>
    );
});

// ============ MAIN SCREEN ============
function SmallGroupDetailsScreen() {
    const [mobileTab, setMobileTab] = useState(0);
    const [activityTab, setActivityTab] = useState(0);
    const [initialPageApplied, setInitialPageApplied] = useState(false);

    const { isNative } = usePlatform();
    const navigation = useNavigation();
    const router = useRouter();
    const { smallGroupID, tab } = useLocalSearchParams();
    const { width } = useWindowDimensions();
    const isWideScreen = width >= 900;

    if (!smallGroupID) {
        router.canGoBack() ? router.back() : router.replace("/");
    }

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: smallGroup, isFetching: fetchingSmallGroup } = Query.smallGroups.byId({ id: smallGroupID });
    const { enabled: bibleStudiesEnabled } = useBibleStudies();

    // Flag-on: Bible Study records route to the Bible Studies module — deep
    // links/search/recents land here and get re-routed to the right surface.
    useEffect(() => {
        if (bibleStudiesEnabled && smallGroup?.SmallGroupType?.purpose === 'BIBLE_STUDY') {
            router.replace(`/biblestudies/${smallGroup.id}`);
        }
    }, [bibleStudiesEnabled, smallGroup?.SmallGroupType?.purpose, smallGroup?.id, router]);

    // Track recently visited
    useEffect(() => {
        if (smallGroup?.id) {
            saveToRecents({
                id: smallGroup.id,
                url: `/smallgroups/${smallGroup.id}`,
                title: smallGroup.name,
                params: { smallGroupID: smallGroup.id },
            });
        }
    }, [smallGroup?.id]);

    const deleteSmallGroupMutation = useMutation({
        mutationFn: deleteSmallGroup,
        onSuccess: () => {
            router.back();
            queryClient.invalidateQueries({ queryKey: ['smallGroups'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            queryClient.invalidateQueries({ queryKey: ['people'] });
        }
    });

    useEffect(() => {
        const options = [];
        
        if (smallGroup?.id && (loggedInUser?.id == smallGroup?.leaderID || loggedInUser?.id == smallGroup?.coLeaderID || loggedInUser?.can?.("Edit All Small Groups"))) {
            options.push({
                name: "Log a Session",
                icon: dropdownIcons.add,
                onSelect: () => router.push({ pathname: 'smallgroups/sessions/create', params: { smallGroupID: smallGroup.id } })
            });
            options.push({
                name: "Edit Small Group",
                icon: dropdownIcons.edit,
                onSelect: () => {
                    const lessonTime = smallGroup?.lessonTime ? Date.parse("2022-01-01 " + smallGroup?.lessonTime) : null;
                    router.push({ pathname: 'smallgroups/create', params: { id: smallGroup.id, lessonTime: lessonTime } });
                }
            });
        }

        if (smallGroup?.id && (loggedInUser?.id == smallGroup?.leaderID || loggedInUser?.id == smallGroup?.coLeaderID || loggedInUser?.can?.("Delete Small Groups"))) {
            options.push({
                name: "Delete Small Group",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: "Delete Small Group",
                    subtitle: "Are you sure you want to delete this Small Group? This will also delete all sessions and you will not be able to recover them.",
                    isDestructive: true,
                    confirmText: "Delete",
                    onConfirm: () => {
                        if (!smallGroup?.id) return;
                        deleteSmallGroupMutation.mutate(smallGroup.id);
                    }
                })
            });
        }

        navigation.setOptions({
            headerTitle: () => <PageTitle title={smallGroup?.name} subtitle={smallGroup?.SmallGroupType?.name} />,
            headerRight: options.length == 0 ? null : () => <DropdownMenu testID="HeaderMenu" options={options} />,
        });
    }, [navigation, smallGroup?.name, smallGroup?.SmallGroupType?.name, smallGroup?.leaderID, smallGroup?.coLeaderID, smallGroup?.id, loggedInUser]);

    // ============ TAB ROUTES ============
    const mobileRoutes = useMemo(() => [
        { key: 'info', title: 'Info' },
        { key: 'sessions', title: 'Sessions' },
        { key: 'students', title: 'Members' }
    ], []);

    const activityRoutes = useMemo(() => [
        { key: 'sessions', title: 'Sessions' },
        { key: 'students', title: 'Members' }
    ], []);

    // Apply tab param (or auto-jump to Sessions if user is the leader)
    useEffect(() => {
        if (initialPageApplied || !smallGroup?.id || !loggedInUser) return;

        const effectiveTab = tab || (loggedInUser?.id == smallGroup?.leaderID ? 'sessions' : null);
        if (!effectiveTab) {
            setInitialPageApplied(true);
            return;
        }

        const mobileIndex = mobileRoutes.findIndex(r => r.key === effectiveTab || r.title === effectiveTab);
        const activityIndex = activityRoutes.findIndex(r => r.key === effectiveTab || r.title === effectiveTab);

        setTimeout(() => {
            if (mobileIndex !== -1) setMobileTab(mobileIndex);
            if (activityIndex !== -1) setActivityTab(activityIndex);
            setInitialPageApplied(true);
        }, 100);
    }, [tab, initialPageApplied, smallGroup?.id, smallGroup?.leaderID, loggedInUser, mobileRoutes, activityRoutes]);

    // ============ TAB SCENES ============
    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderActivityScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'sessions':
                return <SessionsTab smallGroupID={smallGroupID} />;
            case 'students':
                return <StudentsTab smallGroupID={smallGroupID} />;
            default:
                return null;
        }
    }, [smallGroupID]);

    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'info') return <SmallGroupInfoPanel smallGroupID={smallGroupID} />;
        return renderActivityScene({ route });
    }, [smallGroupID, renderActivityScene]);

    // ============ LOADING STATE ============
    const renderLoadingState = () => (
        <Animated.View entering={FadeInDown.duration(300)} style={styles.loadingContainer}>
            <View style={styles.loadingHeaderRow}>
                <ContentLoader containerHeight={52} containerWidth={52} borderRadius={10} spacingVertical={0} />
                <View style={styles.loadingHeaderInfo}>
                    <ContentLoader containerHeight={22} containerWidth="70%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                    <ContentLoader containerHeight={14} containerWidth="40%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                </View>
            </View>
            <View style={styles.loadingStatsRow}>
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
            </View>
            <ContentLoader containerHeight={180} borderRadius={12} staggerPosition={4} />
        </Animated.View>
    );

    // ============ MAIN RENDER ============
    if (!smallGroup?.id && fetchingSmallGroup) return renderLoadingState();
    if (!smallGroup?.id && !fetchingSmallGroup) return <EmptyListPlaceholder type="404" message="Small Group not found" />;

    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    <SmallGroupInfoPanel smallGroupID={smallGroupID} />
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: activityTab, routes: activityRoutes }}
                            renderScene={renderActivityScene}
                            onIndexChange={setActivityTab}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: mobileTab, routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={setMobileTab}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />
        </View>
    );
}


const styles = StyleSheet.create((theme, rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    
    // Loading State
    loadingContainer: { flex: 1, padding: 20, maxWidth: 500, width: rt.isLandscape ? '50%' : '100%' },
    loadingHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 20 },
    loadingHeaderInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
    loadingStatsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingStatItem: { flex: 1 },

    // Two Column Layout
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },

    // Header Card
    headerCard: { 
        backgroundColor: theme.colors.surface, 
        borderRadius: 16, 
        padding: 20, 
        marginBottom: 16, 
        shadowColor: '#000', 
        shadowOffset: { width: 0, height: 2 }, 
        shadowOpacity: 0.06, 
        shadowRadius: 8, 
        elevation: 3 
    },

    // Type Badge
    typeBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.groupsContainer,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
        marginBottom: 16,
        alignSelf: 'flex-start',
        gap: 6
    },
    typeBadgeText: { color: theme.colors.groups, fontSize: 12, fontFamily: theme.fonts.bold },

    // Title Row
    titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    iconBadge: {
        width: 52,
        height: 52,
        backgroundColor: theme.colors.groupsContainer,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center'
    },
    titleInfo: { flex: 1, marginLeft: 14 },
    groupTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    leaderSubtitle: { fontSize: 13, color: theme.colors.groups, marginTop: 2 },

    // Stats Row
    statsRow: { 
        flexDirection: 'row', 
        alignItems: 'center', 
        paddingVertical: 14, 
        borderTopWidth: 1, 
        borderBottomWidth: 1, 
        borderColor: theme.colors.border, 
        marginBottom: 16 
    },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Status Pill
    statusPill: (bgColor, textColor) => ({
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors[bgColor],
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        gap: 4,
        borderWidth: 1,
        borderColor: theme.colors[textColor]
    }),
    statusPillText: (textColor) => ({
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors[textColor],
        textTransform: 'capitalize'
    }),

    // Leader Row
    leaderRow: { 
        flexDirection: 'row', 
        alignItems: 'center', 
        gap: 12, 
        paddingBottom: 16, 
        borderBottomWidth: 1, 
        borderBottomColor: theme.colors.border,
        marginBottom: 16
    },
    leaderInfo: { flex: 1 },
    leaderLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase' },
    leaderName: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },
    leaderRowInline: { flexDirection: 'row', alignItems: 'center', gap: 8 },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

    // Info Section
    infoSection: { 
        backgroundColor: theme.colors.surface, 
        borderRadius: 16, 
        padding: 16, 
        marginBottom: 16,
        shadowColor: '#000', 
        shadowOffset: { width: 0, height: 1 }, 
        shadowOpacity: 0.04, 
        shadowRadius: 4, 
        elevation: 2 
    },
    sectionLabel: { 
        fontSize: 12, 
        fontFamily: theme.fonts.bold, 
        color: theme.colors.textSecondary, 
        textTransform: 'uppercase', 
        marginBottom: 12 
    },
    infoRow: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        paddingVertical: 14, 
        borderBottomWidth: 1, 
        borderBottomColor: theme.colors.border 
    },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 140 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    linkText: { fontSize: 14, color: theme.colors.groups, textAlign: 'right' },
    emptyText: { fontSize: 14, color: theme.colors.textSecondary, fontStyle: 'italic', textAlign: 'right' },

    // Members Preview
    studentsPreview: {
        backgroundColor: theme.colors.surface, 
        borderRadius: 16, 
        padding: 16,
        shadowColor: '#000', 
        shadowOffset: { width: 0, height: 1 }, 
        shadowOpacity: 0.04, 
        shadowRadius: 4, 
        elevation: 2 
    },
    studentsAvatarRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    moreBadge: { 
        width: 36, 
        height: 36, 
        borderRadius: 18, 
        backgroundColor: theme.colors.border, 
        alignItems: 'center', 
        justifyContent: 'center' 
    },
    moreBadgeText: { fontSize: 11, color: theme.colors.textSecondary, fontFamily: theme.fonts.bold },

    // Tab Content
    tabContent: { flex: 1, paddingVertical: 16 },
    listPadding: { padding: 16, paddingBottom: 100 },
}));

export default SmallGroupDetailsScreen;
